import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
  OnInit,
} from "@angular/core";
import { FormioCustomComponent, FormioEvent } from "@formio/angular";
import { ProjectPlanData } from "../models/projectPlanData";
import { MessageService } from "primeng/api";
import { CellEditor, TableEditCancelEvent, TableEditInitEvent } from "primeng/table";
import { DateTime } from 'luxon';
import { DatePipe } from "@angular/common";

@Component({
  selector: "projectPlan",
  templateUrl: "./projectPlan.component.html",
  styleUrls: ["./projectPlan.component.scss"],
})
export class ProjectPlanComponent implements OnInit, FormioCustomComponent<string> {
  projectPlanData: ProjectPlanData[] = [];

  totalIsValid = true;

  private initialized = false;

  private _value: string;
  @Input()
  public set value(v: string) {
    this._value = v;
    this.init();
  }

  public get value(): string {
    return this._value;
  }

  public get isEmpty(): boolean {
    // No value available or the value contains no project plan row/s
    return !this.value || this.projectPlanData.length === 0 ||
    // The value contains one project plan row, which is the default/empty one
    ( 
      this.projectPlanData.length === 1 &&
      !this.projectPlanData[0].description &&
      !this.projectPlanData[0].startDate &&
      !this.projectPlanData[0].startDateObj &&
      !this.projectPlanData[0].endDate &&
      !this.projectPlanData[0].endDateObj &&
      !this.projectPlanData[0].durationInDays
    );
  }

  public get isValid(): boolean {
    let result = false;
    if (!this.required && (this.isEmpty || this.checkValueIsValid())) {
      result = true;
    }
    else if (this.required && !this.isEmpty && this.checkValueIsValid()) {
      result = true;
    }
    return result;
  }

  @Output()
  valueChange = new EventEmitter<string>();

  @Output()
  formioEvent = new EventEmitter<FormioEvent>();

  @Input()
  disabled: boolean;

  @Input()
  required: boolean;

  @Input()
  totalRequirements: string;

  @Input()
  maxRows: number;

  @Input()
  maxDescriptionWords: number;

  @Input()
  descriptionHeader: string;

  @Input()
  descriptionPlaceholder: string;

  @Input()
  startDateHeader: string;

  @Input()
  earliestStartDate: any;

  @Input()
  endDateHeader: string;

  @Input()
  latestEndDate: string;

  @Input()
  durationHeader: string;

  @Input()
  maxDurationInDays: string;

  @Input()
  maxDurationInDaysValidationRule: string;


  setupEarliestStartDateObj: any = null;
  setupLatestEndDateObj: any = null;

  earliestSelectedStartDate: string = null;
  earliestSelectedStartDateObj: any = null
  latestSelectedEndDate: string = null;
  latestSelectedEndDateObj: any = null;


  customMessage: string =
    "Please revise your project plan table. Some of the requirements are not met.";

  selectedProjectPlanRows: ProjectPlanData[];
  total: number = 0;

  errors: string[] = [];

  emptyFieldsErrorMessage = 'All fields are required.';
  totalDurationRequirementErrorMessage = 'The total duration requirement is not met.';
  

  constructor(
    private messageService: MessageService,
    private datePipe: DatePipe,
  ) {
    this.selectedProjectPlanRows = [];
  }

  ngOnInit(): void {
    
  }

  private init() {
    if (!this.projectPlanData.length) {
      let defaultNumberOfRows = 3;
      if (this.maxRows < 3) {
        defaultNumberOfRows = this.maxRows;
      }

      for (let index = 0; index < defaultNumberOfRows; index++) {
        this.projectPlanData.push({
          id: index,
          description: "",
          startDate: "",
          startDateObj: null,
          startDateJsDate: null,
          endDate: "",
          endDateObj: null,
          endDateJsDate: null,
          durationInDays: 0,
        });
      }
    };
    // convert earliest start date (from setup) to js date
    if (this.earliestStartDate) {
      // get earliest start date and extract the date from it
      // then assign 00:00 as time for it, because the date comes with a time from the config
      let tempEarliestStartDate = this.earliestStartDate.split('T')[0] + 'T00:00:00+00:00';
      // create a new JS date from it
      let tempEarliestStartDateObj = new Date(tempEarliestStartDate);
      // create a new JS date using the time (in ms) and negating the timezone offset of the user
      // the result from this is a JS date with the correct day and 00:00 hours
      this.setupEarliestStartDateObj = new Date(tempEarliestStartDateObj.getTime() + (tempEarliestStartDateObj.getTimezoneOffset() * 60 * 1000));
      // the whole thing above allows users to select the earliest start date as a valid date
      // and the datepicker is populated correctly
    }
    // convert latest end date (from setup) to js date
    if (this.latestEndDate) {
      // the same issue as earliestStartDate can appear here as well, although less likely
      // as a failsafe, perform the same calculations
      // end result from them is a js date with the same day as in the config, with 00:00 hours
      let tempLatestEndDate = this.latestEndDate.split('T')[0] + 'T00:00:00+00:00';
      let tempLatestEndDateDateObj = new Date(tempLatestEndDate);
      this.setupLatestEndDateObj = new Date(tempLatestEndDateDateObj.getTime() + (tempLatestEndDateDateObj.getTimezoneOffset() * 60 * 1000));
    }

    if (this.value && !this.initialized) {

      this.projectPlanData = JSON.parse(this.value).value;
      this.projectPlanData.forEach(element => {
        delete element['selected'];
      });

      this.calculationsOnInit();

      this.getTotal();
      this.checkValueIsValid();
      this.initialized = true;
    }
  }

  calculationsOnInit() {
    this.projectPlanData.forEach(projectPlanRow => {

      if (projectPlanRow.startDateObj) {
        this.calculateEarliestStartDate();
        projectPlanRow.startDateJsDate = new Date(projectPlanRow.startDateObj);
      } else {
        projectPlanRow.startDateJsDate = null;
      }

      if (projectPlanRow.endDateObj) {
        this.calculateLatestEndDate();
        projectPlanRow.endDateJsDate = new Date(projectPlanRow.endDateObj);
      } else {
        projectPlanRow.endDateJsDate = null;
      }
    })
  }

  private setValue(val: string = null) {
    this.getTotal();
    const valid = this.checkValueIsValid();
    if (valid) {
      this.clearError();
    }

    const projectPlanDataToSave = {
      // avoid two way binding
      value: JSON.parse(JSON.stringify(this.projectPlanData)),
      valid: valid,
    };

    projectPlanDataToSave.value.forEach(element => {
      delete element['selected'];
      delete element['startDateJsDate'];
      delete element['endDateJsDate'];
    });

    this.performErrorChecks();
    this.value = JSON.stringify(projectPlanDataToSave);
    this.valueChange.emit(this.value);
    this.formioEvent.emit({ eventName: "change", data: { isChanged: true } });
  }

  deleteSelectedProjectPlanRows() {
    if (this.projectPlanData.length == this.selectedProjectPlanRows.length) {
      return;
    }
    this.projectPlanData = this.projectPlanData.filter(
      (val) => !this.selectedProjectPlanRows.includes(val)
    );
    this.selectedProjectPlanRows = [];
    this.getTotal();
    this.handleChange();
  }

  addNew() {
    if (this.projectPlanData.length == this.maxRows) {
      return;
    }
    if (this.projectPlanData.length) {
      this.projectPlanData.push({
        id: this.projectPlanData[this.projectPlanData.length - 1].id + 1,
        description: "",
        startDate: "",
        startDateObj: null,
        startDateJsDate: null,
        endDate: "",
        endDateObj: null,
        endDateJsDate: null,
        durationInDays: 0,
      });
    } else {
      this.projectPlanData.push({
        id: 0,
        description: "",
        startDate: "",
        startDateObj: null,
        startDateJsDate: null,
        endDate: "",
        endDateObj: null,
        endDateJsDate: null,
        durationInDays: 0,
      });
    }
  }

  performErrorChecks() {
    this.errors = []; // reset before validation
    if (!this.checkRowsAreValid()) {
      this.errors.push(this.emptyFieldsErrorMessage);
    }

    if (!this.validateTotal()) {
      this.errors.push(this.totalDurationRequirementErrorMessage);
    }
  };

  checkValueIsValid() {
    this.validateRows();
    if (!this.validateTotal() || !this.checkRowsAreValid()) {
      return false;
    }

    return true;
  }

  checkRowsAreDefault() {
    for (let i = 0; i < this.projectPlanData.length; i++) {
      if (
        this.projectPlanData[i].description !== "" ||
        this.projectPlanData[i].startDate !== "" ||
        this.projectPlanData[i].startDateObj !== null ||
        this.projectPlanData[i].endDate !== "" ||
        this.projectPlanData[i].endDateObj !== null ||
        this.projectPlanData[i].durationInDays !== null
      ) {
        return false;
      }
    }
    return true;
  }

  validateTotal() {
    if (this.maxDurationInDaysValidationRule === "=") {
      let totalValueNum = Number(this.maxDurationInDays);
      if (this.total !== totalValueNum) {
        this.totalIsValid = false;
        return false;
      }
    }

    if (this.maxDurationInDaysValidationRule === ">=") {
      let totalValueNum = Number(this.maxDurationInDays);
      if (this.total < totalValueNum) {
        this.totalIsValid = false;
        return false;
      }
    }

    if (this.maxDurationInDaysValidationRule === "<=") {
      let totalValueNum = Number(this.maxDurationInDays);
      if (this.total === 0 || this.total > totalValueNum) {
        this.totalIsValid = false;
        return false;
      }
    }

    if (this.maxDurationInDaysValidationRule === ">=, <=") {
      let numbers = this.maxDurationInDays.split(",");
      let min = Number(numbers[0]);
      let max = Number(numbers[1]);

      if (min > this.total || this.total > max) {
        this.totalIsValid = false;
        return false;
      }
    }

    this.totalIsValid = true;
    return true;
  }

  checkRowsAreValid() {
    for (let i = 0; i < this.projectPlanData.length; i++) {
      if (
        !this.projectPlanData[i].description ||
        !this.projectPlanData[i].startDate ||
        !this.projectPlanData[i].endDate ||
        !this.projectPlanData[i].durationInDays
      ) {
        return false;
      }
    }
    return true;
  }

  validateRows() {
    let rowsAreValid = [];
    for (let i = 0; i < this.projectPlanData.length; i++) {
      if (
        this.validateProjectPlanRowDescription(i) &&
        this.validateProjectPlanRowStartDate(i) &&
        this.validateProjectPlanRowEndDate(i)
      ) {
        rowsAreValid.push(true);
      } else {
        rowsAreValid.push(false);
      }
    }
    // if all rows are true = rows are valid
    // if any row is false = rows are invalid
    let rowsAreValidFinal = rowsAreValid.some(el => el === false) ? false : true;
    return rowsAreValidFinal;
  }

  validateProjectPlanRowDescription(i: number) {
    if (this.projectPlanData[i].description === "") {
      return false;
    } else {
      return true;
    }
  }

  validateProjectPlanRowStartDate(i: number) {
    if (this.projectPlanData[i].startDate === "") {
      return false;
    } else {
      return true;
    }
  }

  validateProjectPlanRowEndDate(i: number) {
    if (this.projectPlanData[i].endDate === "") {
      return false;
    } else {
      return true;
    }
  }

  removeLastWords(projectPlanRow) {
    let res = projectPlanRow.description.split(/\s+/);
    if (res.length > this.maxDescriptionWords) {
      res = res.splice(0, this.maxDescriptionWords);
    }
    projectPlanRow.description = res.join(" ");
  }

  handleStartDateChange(event, projectPlanRow) {
    // if startDateObj exists - do assignment and calculations
    if (projectPlanRow.startDateObj) {
      projectPlanRow.startDateJsDate = new Date(projectPlanRow.startDateObj);
      projectPlanRow.startDate = this.datePipe.transform(projectPlanRow.startDateObj, 'yyyy-MM-ddTHH:mm:ss');
    } else {
      // probably clear button is clicked, or the datepicker is cleared manually
      projectPlanRow.startDate = '';
      projectPlanRow.startDateJsDate = null;
    }

    if (projectPlanRow.startDate && projectPlanRow.endDate) {
      this.calculateDuration(projectPlanRow);
    } else {
      projectPlanRow.durationInDays = 0;
    }

    // calculate earliest start date on every change
    this.calculateEarliestStartDate();

    this.handleChange();
  }

  handleEndDateChange(event, projectPlanRow) {
    // if endDateObj exists - do assignment and calculations
    if (projectPlanRow.endDateObj) {
      projectPlanRow.endDateJsDate = new Date(projectPlanRow.endDateObj);
      projectPlanRow.endDate = this.datePipe.transform(projectPlanRow.endDateObj, 'yyyy-MM-ddTHH:mm:ss');
    } else {
      // probably clear button is clicked, or the datepicker is cleared manually
      projectPlanRow.endDate = '';
      projectPlanRow.endDateJsDate = null;
    }

    if (projectPlanRow.startDate && projectPlanRow.endDate) {
      this.calculateDuration(projectPlanRow);
    } else {
      projectPlanRow.durationInDays = 0;
    }

    // calculate latest end date on every change
    this.calculateLatestEndDate();

    this.handleChange();
  }

  calculateEarliestStartDate() {
    // temp vars
    let tempEarliestSelectedStartDate = "";
    let tempEarliestSelectedStartDateObj = null;

    this.projectPlanData.forEach(projectPlanRow => {
      if (tempEarliestSelectedStartDate) {
        // if temp date exists
        if (projectPlanRow.startDate && projectPlanRow.startDate < tempEarliestSelectedStartDate) {
          // if start date exists and it's earlier than the temp date - assign it
          tempEarliestSelectedStartDate = projectPlanRow.startDate;
          tempEarliestSelectedStartDateObj = projectPlanRow.startDateObj;
        }
      } else {
        // if temp date does not exist
        if (projectPlanRow.startDate) {
          // if start date exists - assign it as the temp date
          tempEarliestSelectedStartDate = projectPlanRow.startDate;
          tempEarliestSelectedStartDateObj = projectPlanRow.startDateObj;
        }
      };
    })
    // assign temp date to the model
    // if it's empty - the model will be empty as well - valid case for no selected dates
    this.earliestSelectedStartDate = tempEarliestSelectedStartDate;
    this.earliestSelectedStartDateObj = tempEarliestSelectedStartDateObj;
  }

  calculateLatestEndDate() {
    // temp vars
    let tempLatestSelectedEndDate = "";
    let tempLatestSelectedEndDateObj = null;

    this.projectPlanData.forEach(projectPlanRow => {
      if (tempLatestSelectedEndDate) {
        // if temp date exists
        if (projectPlanRow.endDate && projectPlanRow.endDate > tempLatestSelectedEndDate) {
          // if start date exists and it's earlier than the temp date - assign it
          tempLatestSelectedEndDate = projectPlanRow.endDate;
          tempLatestSelectedEndDateObj = projectPlanRow.endDateObj;
        }
      } else {
        // if temp date does not exist
        if (projectPlanRow.endDate) {
          // if start date exists - assign it as the temp date
          tempLatestSelectedEndDate = projectPlanRow.endDate;
          tempLatestSelectedEndDateObj = projectPlanRow.endDateObj;
        }
      };
    })
    // assign temp date to the model
    // if it's empty - the model will be empty as well - valid case for no selected dates
    this.latestSelectedEndDate = tempLatestSelectedEndDate;
    this.latestSelectedEndDateObj = tempLatestSelectedEndDateObj;
  }

  calculateDuration(projectPlanRow) {
    let startDate = DateTime.fromISO(projectPlanRow.startDate);
    let endDate = DateTime.fromISO(projectPlanRow.endDate);

    // add one extra day to include the currently selected end date day
    projectPlanRow.durationInDays = endDate.diff(startDate, ["days"]).toObject().days + 1;
  }

  getTotal() {
    var sum = 0;
    for (let i = 0; i < this.projectPlanData.length; i++) {
      if (this.projectPlanData[i].durationInDays) {
        sum += this.projectPlanData[i].durationInDays;
      }
    }

    if (sum > 0) {
      this.total = sum;
      return `${this.total} Days`; 
    } else {
      this.total = 0;
      return '';
    }
  }

  handleChange() {
    this.setValue();
  }

  private clearError() {
    setTimeout(() => {
      var errors = document.getElementsByClassName("error");
      if (errors.length > 1) {
        for (let i = 0; i < errors.length; i++) {
          if (errors[i].innerHTML === this.customMessage) {
            errors[i].setAttribute("style", "display:none;");
            break;
          }
        }
      }
    }, 250);
  }

  onRowSelect(typeOfSelection, item) {
    if (typeOfSelection == true) {
      this.selectedProjectPlanRows.push(item);
    } else if (typeOfSelection == false) {
      let removeIndex = this.selectedProjectPlanRows.map(el => el.id).indexOf(item.id);
      if (removeIndex >= 0) {
        this.selectedProjectPlanRows.splice(removeIndex, 1);
      };
    }
  }

  showDeleteTooltip(selectedProjectPlanRowsLength) {
    let tooltip: string = ``;
    if (selectedProjectPlanRowsLength === 0) {
      tooltip = 'You must select at least one row to delete'
    } else {
      if (selectedProjectPlanRowsLength === this.projectPlanData.length) {
        tooltip = 'You must leave at least one row present'
      };
    }
    return tooltip;
  }
}
